import React from "react";
import { Box, Typography } from "@mui/material";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import {
  animationPreviewUrlBySlug,
  petAnimationUrlBySlug,
  petImageUrlBySlug,
} from "config/vetstoreAssets";
import { highQualityLottieProps } from "config/lottieQuality";
import { StoreItem } from "types/vetcoin";

const DotLottieReactComponent = DotLottieReact as unknown as React.ComponentType<any>;

type StoreItemPreviewProps = {
  item: Pick<StoreItem, "type" | "slug"> & Partial<StoreItem>;
  size?: "small" | "large";
  chromeless?: boolean;
};

const petEmojiBySlug: Record<string, string> = {
  "clinic-cat": "🐱",
  "paw-pup": "🐶",
  "pocket-parrot": "🦜",
  "capy-care": "🦫",
  "polar-fox": "🦊",
  "hedge-doc": "🦔",
  "aurora-wolf": "🐺",
  "nebula-otter": "🦦",
  "crimson-panda": "🐼",
  "celestial-jaguar": "🐆",
  "phoenix-vet": "🔥",
};

const rarityBackground: Record<string, string> = {
  COMMON: "linear-gradient(135deg, #e7eff7 0%, #f7fafc 100%)",
  UNCOMMON: "linear-gradient(135deg, #d8f0ff 0%, #eef8ff 100%)",
  RARE: "linear-gradient(135deg, #ece3ff 0%, #f6f2ff 100%)",
  EPIC: "linear-gradient(135deg, #ffe8cf 0%, #fff4e7 100%)",
  LEGENDARY: "linear-gradient(135deg, #ffe2cc 0%, #fff0e4 100%)",
};

export const getPetEmoji = (slug?: string) => {
  if (!slug) return "🐾";
  return petEmojiBySlug[slug] || "🐾";
};

const AnimationPreview = ({ slug }: { slug: string }) => {
  const animationSrc = animationPreviewUrlBySlug[slug];
  if (animationSrc) {
    return (
      <DotLottieReactComponent
        src={animationSrc}
        style={{ width: "100%", height: "100%", display: "block" }}
        {...highQualityLottieProps}
      />
    );
  }

  const variant = slug.includes("pulse")
    ? "pulse"
    : slug.includes("comet")
      ? "comet"
      : slug.includes("wave")
        ? "wave"
        : "spark";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 30% 25%, rgba(25,118,210,0.18) 0%, rgba(25,118,210,0) 50%)",
        }}
      />

      {(variant === "spark" || variant === "wave") && (
        <>
          <Box
            sx={{
              position: "absolute",
              left: "18%",
              top: "28%",
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: "primary.main",
              animation: "sparkFloat 1.4s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: "48%",
              top: "42%",
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "secondary.main",
              animation: "sparkFloat 1.8s ease-in-out infinite",
              animationDelay: "0.25s",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: "70%",
              top: "26%",
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: "#ff9800",
              animation: "sparkFloat 1.6s ease-in-out infinite",
              animationDelay: "0.4s",
            }}
          />
        </>
      )}

      {variant === "pulse" && (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: "2px solid",
            borderColor: "primary.main",
            transform: "translate(-50%, -50%)",
            animation: "pulseRing 1.3s ease-out infinite",
          }}
        />
      )}

      {variant === "comet" && (
        <Box
          sx={{
            position: "absolute",
            top: "55%",
            left: "-22%",
            width: "34%",
            height: 4,
            borderRadius: 999,
            background:
              "linear-gradient(90deg, rgba(25,118,210,0), rgba(25,118,210,0.8))",
            animation: "cometTrail 1.3s linear infinite",
          }}
        />
      )}

      <Box
        sx={{
          "@keyframes sparkFloat": {
            "0%": { transform: "translateY(0px)", opacity: 0.55 },
            "50%": { transform: "translateY(-6px)", opacity: 1 },
            "100%": { transform: "translateY(0px)", opacity: 0.55 },
          },
          "@keyframes pulseRing": {
            "0%": { transform: "translate(-50%, -50%) scale(0.6)", opacity: 0.9 },
            "100%": {
              transform: "translate(-50%, -50%) scale(3)",
              opacity: 0,
            },
          },
          "@keyframes cometTrail": {
            "0%": { transform: "translateX(0)" },
            "100%": { transform: "translateX(440%)" },
          },
        }}
      />
    </Box>
  );
};

export const StoreItemPreview = ({
  item,
  size = "large",
  chromeless = false,
}: StoreItemPreviewProps) => {
  const isSmall = size === "small";
  const animationPreviewHeight = isSmall ? 42 : 88;
  const petPreviewHeight = isSmall ? 52 : 122;
  const petEmoji = getPetEmoji(item.slug);
  const backgroundByRarity = item.rarity
    ? rarityBackground[item.rarity]
    : undefined;
  const petImageSrc = item.slug ? petImageUrlBySlug[item.slug] : undefined;
  const petAnimationSrc = item.slug ? petAnimationUrlBySlug[item.slug] : undefined;
  const animationPreviewBackground =
    item.slug === "aurora-wave" || item.slug === "soft-pulse"
      ? "linear-gradient(135deg, #e6eefc 0%, #f5f8ff 100%)"
      : "linear-gradient(135deg, #17263f 0%, #0f1a2e 100%)";

  if (item.type === "ANIMATION") {
    return (
      <Box
        sx={{
          width: "100%",
          height: animationPreviewHeight,
          borderRadius: isSmall ? 1.5 : 2,
          border: chromeless ? "none" : "1px solid",
          borderColor: chromeless ? "transparent" : "divider",
          background: chromeless ? "transparent" : animationPreviewBackground,
          mb: isSmall ? 0 : 1.5,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <AnimationPreview slug={item.slug} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: chromeless ? "100%" : petPreviewHeight,
        aspectRatio: chromeless ? "1 / 1" : undefined,
        borderRadius: chromeless ? 0 : isSmall ? 1.5 : 2,
        border: chromeless ? "none" : "1px solid",
        borderColor: chromeless ? "transparent" : "divider",
        background: chromeless
          ? "transparent"
          : backgroundByRarity || "linear-gradient(135deg, #f5f7fb 0%, #ffffff 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        m: 0,
        mb: chromeless || isSmall ? 0 : 1.5,
        p: 0,
        overflow: chromeless ? "visible" : "hidden",
      }}
    >
        {petAnimationSrc ? (
          <DotLottieReactComponent
            src={petAnimationSrc}
            style={{
              width: chromeless ? "100%" : isSmall ? 54 : 96,
              height: chromeless ? "100%" : isSmall ? 54 : 96,
              display: "block",
            }}
            {...highQualityLottieProps}
          />
        ) : petImageSrc ? (
          <Box
            component="img"
            src={petImageSrc}
            alt={item.name || "VetPet"}
            decoding="async"
            sx={{
              width: chromeless ? "100%" : isSmall ? 24 : 48,
              height: chromeless ? "100%" : isSmall ? 24 : 48,
              objectFit: "contain",
              backfaceVisibility: "hidden",
              transform: "translateZ(0)",
            }}
          />
        ) : (
          <Typography sx={{ fontSize: isSmall ? "1.25rem" : "2rem", lineHeight: 1 }}>
            {petEmoji}
          </Typography>
        )}
    </Box>
  );
};
